import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Loader } from 'lucide-react';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const CreatePayout = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    reference_id: '',
    amount: '',
    mode: 'IMPS',
    beneficiary_name: '',
    beneficiary_vpa: '',
    beneficiary_account: '',
    beneficiary_ifsc: '',
    beneficiary_phone: '',
    beneficiary_email: '',
    remarks: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      // Validation
      if (!formData.reference_id || formData.reference_id.trim().length === 0) {
        toast.error('Please enter a reference ID');
        return;
      }

      if (!formData.amount || parseFloat(formData.amount) <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }

      if (!formData.beneficiary_name) {
        toast.error('Please enter beneficiary name');
        return;
      }

      // Bank transfer mode validation
      if (!formData.beneficiary_account || !formData.beneficiary_ifsc) {
        toast.error('Please enter beneficiary account and IFSC code');
        return;
      }

      // Prepare payout data
      const payoutData = {
        reference_id: formData.reference_id.trim(),
        amount: parseFloat(formData.amount),
        mode: formData.mode,
        beneficiary_name: formData.beneficiary_name,
        beneficiary_account: formData.beneficiary_account,
        beneficiary_ifsc: formData.beneficiary_ifsc,
        beneficiary_phone: formData.beneficiary_phone || undefined,
        beneficiary_email: formData.beneficiary_email || undefined,
        remarks: formData.remarks || undefined
      };

      const response = await apiService.initiatePayout(payoutData);

      if (response.success) {
        const data = response.data;

        // Show different messages based on approval status
        if (data.approval_status === 'awaiting_approval') {
          toast.success('Payout created and pending admin approval', { duration: 5000 });
        } else if (data.approval_status === 'auto_approved') {
          toast.success('Payout approved and initiated successfully');
        } else {
          toast.success('Payout initiated successfully');
        }

        // Show warning if risk flagged
        if (data.risk_flagged) {
          toast.warning('Payout flagged for review by risk engine', { duration: 4000 });
        }

        // Show any validation warnings
        if (data.warnings) {
          toast.warning(data.warnings, { duration: 4000 });
        }

        navigate('/payouts');
      } else {
        toast.error(response.error?.message || 'Failed to initiate payout');
      }

    } catch (error) {
      console.error('Payout creation failed:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to initiate payout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/payouts')}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft size={16} className="mr-1" />
          Back to Payouts
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Create Payout</h1>
        <p className="mt-1 text-sm text-gray-600">
          Send money to bank accounts via IMPS, NEFT, or RTGS
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Payout Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Reference ID */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reference ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="reference_id"
                value={formData.reference_id}
                onChange={handleChange}
                placeholder="Enter unique reference ID (e.g., INV-12345)"
                required
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Unique identifier for this payout (used for duplicate detection)
              </p>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount (INR) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="Enter amount"
                step="0.01"
                min="0"
                required
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>

            {/* Mode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payout Mode <span className="text-red-500">*</span>
              </label>
              <select
                name="mode"
                value={formData.mode}
                onChange={handleChange}
                required
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="IMPS">IMPS</option>
                <option value="NEFT">NEFT</option>
                <option value="RTGS">RTGS</option>
              </select>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Beneficiary Details</h2>

          <div className="space-y-4">
            {/* Beneficiary Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Beneficiary Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="beneficiary_name"
                value={formData.beneficiary_name}
                onChange={handleChange}
                placeholder="Enter beneficiary name"
                required
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>

            {/* Bank Account Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="beneficiary_account"
                  value={formData.beneficiary_account}
                  onChange={handleChange}
                  placeholder="Enter account number"
                  required
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  IFSC Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="beneficiary_ifsc"
                  value={formData.beneficiary_ifsc}
                  onChange={handleChange}
                  placeholder="e.g., SBIN0001234"
                  required
                  maxLength={11}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* Phone and Email (Optional) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number (Optional)
                </label>
                <input
                  type="tel"
                  name="beneficiary_phone"
                  value={formData.beneficiary_phone}
                  onChange={handleChange}
                  placeholder="+919876543210"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email (Optional)
                </label>
                <input
                  type="email"
                  name="beneficiary_email"
                  value={formData.beneficiary_email}
                  onChange={handleChange}
                  placeholder="beneficiary@example.com"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* Remarks */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remarks (Optional)
              </label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                placeholder="Enter any remarks or notes"
                rows={3}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/payouts')}
            className="btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary inline-flex items-center"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader size={16} className="mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Send size={16} className="mr-2" />
                Initiate Payout
              </>
            )}
          </button>
        </div>
      </form>

      {/* Info Card */}
      <div className="mt-6 card p-4 bg-blue-50 border border-blue-200">
        <h3 className="text-sm font-medium text-blue-900 mb-2">Important Notes</h3>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Ensure sufficient wallet balance before initiating payout</li>
          <li>IMPS payouts are processed within 30 minutes</li>
          <li>NEFT/RTGS payouts may take 2-4 hours depending on bank working hours</li>
          <li>Payout fees will be deducted from your wallet balance</li>
        </ul>
      </div>
    </div>
  );
};

export default CreatePayout;
